// "Új sablon": an empty letter, or one brought in from HTML or MJML — or, as
// "Másolat készítése", another template's newest version under a new name.
//
// HTML goes through the import door (4.2): the document wrapper, <style> and
// <script> come off, each removal reported, the body kept whole. MJML is not
// what that door is for — it refuses MJML tags on purpose — so an MJML file
// becomes a new template whose next version is that source: two versions, the
// empty first one and the import, both in the history. A copy is one request
// (`POST /templates/{id}/copy`): the new template's v1 is the source's newest
// version, and the source is not touched.

import { useEffect, useId, useMemo, useState } from "react";
import type { FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { FileUpIcon } from "lucide-react";
import { copyTemplate, createTemplate, importHtml, saveDraft } from "@/lib/api";
import type { Template } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/ui/native-select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Field } from "@/components/emc/Field";
import { Callout, ErrorState } from "@/components/emc/states";
import { KEY_SHAPE, copyKey, copyName, keyFromName, languageName, messageOf } from "@/pages/templates/model";
import type { CopySource } from "@/pages/templates/model";

type Mode = "blank" | "import";

const MJML_DOCUMENT = /^\s*(?:<\?xml[^>]*>\s*)?(?:<!--[\s\S]*?-->\s*)*<mjml[\s>]/i;

/** What the editor is told about the way the template came in. */
export interface ArrivalNote {
  imported?: "html" | "mjml";
  warnings?: string[];
  /** A copy: the original's name and the version it was made from. */
  copied?: { name: string; version: number | null };
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The languages the sending accounts speak. */
  languages: string[];
  /** Called when a template was created but could not be opened with its import. */
  onCreated?: () => void;
  /** Set: the dialog makes a copy of this template instead. */
  copyOf?: CopySource | null;
  /** Opens the copy; the default is the router's `navigate`. The editor passes
   * its own, past its unsaved-work question, which was asked before. */
  openCopy?: (template: Template, note: ArrivalNote) => void;
}

export function NewTemplateDialog({ open, onOpenChange, languages, onCreated, copyOf = null, openCopy }: Props) {
  const navigate = useNavigate();
  const ids = useId();
  const [mode, setMode] = useState<Mode>("blank");
  const [name, setName] = useState("");
  const [key, setKey] = useState("");
  const [keyTouched, setKeyTouched] = useState(false);
  const [language, setLanguage] = useState("hu");
  const [markup, setMarkup] = useState("");
  const [fileName, setFileName] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setMode("blank");
    setName(copyOf ? copyName(copyOf.name) : "");
    setKey(copyOf ? copyKey(copyOf.key, copyOf.takenKeys) : "");
    setKeyTouched(copyOf !== null);
    if (copyOf) setLanguage(copyOf.defaultLanguage);
    setMarkup("");
    setFileName(null);
    setError(null);
    setBusy(false);
  }, [open, copyOf]);

  // A copy's base language is one it has wording in; a new letter's, one an
  // account speaks.
  const choices = useMemo(
    () => (copyOf ? [...new Set([copyOf.defaultLanguage, ...copyOf.languages])] : languages),
    [copyOf, languages],
  );
  useEffect(() => {
    if (choices.length > 0 && !choices.includes(language)) setLanguage(choices[0]!);
  }, [choices, language]);

  const derivedKey = keyTouched ? key : keyFromName(name);
  const isMjml = MJML_DOCUMENT.test(markup);
  const keyProblem =
    derivedKey !== "" && !KEY_SHAPE.test(derivedKey)
      ? "Legalább 2 karakter: kisbetű, szám, pont, kötőjel vagy aláhúzás, betűvel vagy számmal kezdve."
      : null;
  const ready =
    name.trim() !== "" && derivedKey !== "" && keyProblem === null && (mode === "blank" || markup.trim() !== "");

  async function readFile(file: File) {
    setFileName(file.name);
    setMarkup(await file.text());
    if (name.trim() === "") setName(file.name.replace(/\.(html?|mjml|txt)$/i, ""));
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!ready || busy) return;
    setBusy(true);
    setError(null);
    const base = { key: derivedKey, name: name.trim(), default_language: language };
    let createdId: number | null = null;
    try {
      if (copyOf !== null) {
        const template = await copyTemplate(copyOf.id, base);
        const note: ArrivalNote = { copied: { name: copyOf.name, version: copyOf.version } };
        if (openCopy) openCopy(template, note);
        else navigate(`/sablonok/${template.id}`, { state: note });
      } else if (mode === "blank") {
        const template = await createTemplate({ ...base, description: "" });
        navigate(`/sablonok/${template.id}`);
      } else if (isMjml) {
        const template = await createTemplate({ ...base, description: "MJML-ből importálva" });
        createdId = template.id;
        await saveDraft(template.id, {
          mjml_source: markup.trim(),
          editor_state: null,
          notes: fileName ? `MJML-ből importálva: ${fileName}` : "MJML-ből importálva",
        });
        navigate(`/sablonok/${template.id}`, { state: { imported: "mjml" } satisfies ArrivalNote });
      } else {
        const result = await importHtml({ ...base, html: markup });
        navigate(`/sablonok/${result.template.id}`, {
          state: { imported: "html", warnings: result.warnings } satisfies ArrivalNote,
        });
      }
      onOpenChange(false);
    } catch (raised: unknown) {
      const message = messageOf(raised);
      setError(
        createdId === null
          ? message
          : `A sablon létrejött, de az MJML nem került bele: ${message} A galériában üresen látod; javítsd a forrást, és próbáld újra egy új sablonnal.`,
      );
      if (createdId !== null) onCreated?.();
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !busy && onOpenChange(next)}>
      <DialogContent className="max-w-panel">
        <DialogHeader>
          <DialogTitle>{copyOf ? "Másolat készítése" : "Új sablon"}</DialogTitle>
          <DialogDescription>
            {copyOf
              ? `„${copyOf.name}” legutóbbi verziója${copyOf.version !== null ? ` (v${copyOf.version})` : ""} új sablonként: a levél, és minden nyelv tárgya, előnézeti szövege és szövegei. Az eredeti nem változik.`
              : "Egy levél, amelyet egyszer raksz össze, és nyelvenként kap tárgyat, előnézeti szöveget és fordítást."}
          </DialogDescription>
        </DialogHeader>

        <form className="flex flex-col gap-4" onSubmit={(event) => void submit(event)}>
          {copyOf?.note && <Callout tone="warn">{copyOf.note}</Callout>}
          {copyOf === null && (
            <Tabs value={mode} onValueChange={(value) => setMode(value as Mode)}>
              <TabsList variant="segmented" aria-label="Honnan indul">
                <TabsTrigger value="blank">Üres levél</TabsTrigger>
                <TabsTrigger value="import">Importálás HTML-ből vagy MJML-ből</TabsTrigger>
              </TabsList>
            </Tabs>
          )}

          <Field label="Név" htmlFor={`${ids}-name`}>
            <Input
              id={`${ids}-name`}
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Black Friday 2026 — főlevél"
              autoFocus
              onFocus={copyOf ? (event) => event.target.select() : undefined}
              required
            />
          </Field>
          <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_auto]">
            <Field
              label="Kulcs"
              htmlFor={`${ids}-key`}
              hint={keyProblem ?? "A kampányok és a flow-k ezzel hivatkoznak rá; később nem változik."}
            >
              <Input
                id={`${ids}-key`}
                className="data-mono"
                value={derivedKey}
                onChange={(event) => {
                  setKeyTouched(true);
                  setKey(event.target.value);
                }}
                aria-invalid={keyProblem !== null ? true : undefined}
                required
              />
            </Field>
            <Field label="Alapnyelv" htmlFor={`${ids}-language`}>
              <NativeSelect
                id={`${ids}-language`}
                value={language}
                onChange={(event) => setLanguage(event.target.value)}
              >
                {choices.map((code) => (
                  <option key={code} value={code}>
                    {languageName(code)}
                  </option>
                ))}
              </NativeSelect>
            </Field>
          </div>

          {copyOf === null && mode === "import" && (
            <Field
              label={isMjml ? "Forrás — MJML-ként ismertük fel" : "Forrás — HTML vagy MJML"}
              htmlFor={`${ids}-markup`}
              hint={
                isMjml
                  ? "Az MJML változatlanul kerül a sablon második verziójába; az első az üres kezdőlevél."
                  : "A régi Klaviyo-levél HTML-je. A <style> és <script> blokkok és a dokumentum burka lekerülnek — minden eltávolítást megmutatunk —, az elemekre írt stílusok maradnak. A kötelező lábléc küldéskor kerül alá."
              }
            >
              <Textarea
                id={`${ids}-markup`}
                className="h-48 font-mono text-label"
                value={markup}
                onChange={(event) => {
                  setMarkup(event.target.value);
                  setFileName(null);
                }}
                placeholder="<!doctype html>… vagy <mjml>…"
                spellCheck={false}
              />
              <label className="inline-flex w-fit cursor-pointer items-center gap-2 text-body text-blue-ink hover:underline">
                <FileUpIcon className="size-4" />
                {fileName ? `Betöltve: ${fileName}` : "Fájl megnyitása (.html, .mjml)"}
                <input
                  type="file"
                  accept=".html,.htm,.mjml,.txt,text/html"
                  className="sr-only"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (file) void readFile(file);
                    event.target.value = "";
                  }}
                />
              </label>
            </Field>
          )}

          {error !== null && <ErrorState message={error} />}

          <DialogFooter>
            <Button variant="outline" type="button" disabled={busy} onClick={() => onOpenChange(false)}>
              Mégse
            </Button>
            <Button type="submit" disabled={!ready || busy}>
              {busy
                ? "Folyamatban…"
                : copyOf
                  ? "Másolat és szerkesztés"
                  : mode === "blank"
                    ? "Létrehozás és szerkesztés"
                    : "Importálás és szerkesztés"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
